import { useMemo } from 'react';

import { Box, Col, Image, Row, Text } from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import type { ILpComponentPartnerCaseStudy } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import Container from './shared/Container';

const CARD_DESKTOP = { padding: '$16x', gap: '$8x' } as const;
const QUOTE_DESKTOP = { typography: '$subheaderXsmall' } as const;
const ATTRIBUTION_DESKTOP = { gap: '$8x' } as const;
const VERIFY_PATTERN = /\s*\[VERIFY[^\]]*\]\s*/gi;

const getImageUrl = (entry: ILpComponentPartnerCaseStudy) => {
  const imageUrl = entry.fields.partnerLogo?.fields?.file?.url;

  return imageUrl ? optimizeContentfulImageUrl(imageUrl, { width: 160, quality: 80 }) : undefined;
};

const getImageAlt = (entry: ILpComponentPartnerCaseStudy) => {
  const { partnerLogo, quoteAuthor, partnerName } = entry.fields;
  return (
    partnerLogo?.fields?.description ||
    partnerLogo?.fields?.title ||
    quoteAuthor ||
    partnerName ||
    ''
  );
};

const hasVerifyToken = (text?: string) => {
  return Boolean(text?.match(VERIFY_PATTERN));
};

const cleanVerifyText = (text?: string) => {
  if (!text) {
    return '';
  }

  return text
    .replace(VERIFY_PATTERN, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const getQuoteRole = (author?: string, role?: string) => {
  if (/\[VERIFY\s+name\]/i.test(author ?? '') && /\[VERIFY\s+title\]/i.test(role ?? '')) {
    return 'VERIFY name + title';
  }

  return cleanVerifyText(role);
};

const VerifyBadge = () => {
  return (
    <Box backgroundColor="$backgroundStateErrorSecondary" borderRadius="$2x" px="$2x" py="$1x">
      <Text tag="span" typography="$labelXsmall" color="$contentStateErrorDefault" display="block">
        VERIFY
      </Text>
    </Box>
  );
};

const CaseStudyImage = ({ imageUrl, alt }: { imageUrl: string; alt: string }) => {
  const source = useMemo(() => ({ uri: imageUrl }), [imageUrl]);

  return <Image source={source} alt={alt} width="$40x" height="$40x" variant="circle" />;
};

export const RenderPartnerCaseStudy = (entry: ILpComponentPartnerCaseStudy) => {
  const { fields } = entry;
  const imageUrl = getImageUrl(entry);
  const quote = cleanVerifyText(fields.quote);
  const quoteAuthor = cleanVerifyText(fields.quoteAuthor);
  const quoteRole = getQuoteRole(fields.quoteAuthor, fields.quoteRole);
  const showVerifyBadge =
    hasVerifyToken(fields.quote) ||
    hasVerifyToken(fields.quoteAuthor) ||
    hasVerifyToken(fields.quoteRole);
  const hasAttribution = quoteAuthor || quoteRole;

  if (!quote && !hasAttribution) {
    return null;
  }

  return (
    <Container noSectionSpacing>
      <Col alignItems="center">
        <Col
          analyticsName={`cosmos-landing-page-partner-case-study-${
            fields.analyticsId ?? entry.sys?.id ?? fields.internalName
          }`}
          trackImpression
          backgroundColor="$backgroundTertiary"
          borderColor="$borderPrimary"
          borderRadius="$8x"
          borderWidth={1}
          gap="$8x"
          maxWidth="$768x"
          padding="$10x"
          width="100%"
          $largerThanSM={CARD_DESKTOP}
        >
          {quote && (
            <Text
              tag="p"
              typography="$bodyLarge"
              fontWeight="$medium"
              color="$contentPrimary"
              display="block"
              $largerThanSM={QUOTE_DESKTOP}
            >
              &ldquo;{quote}&rdquo;
            </Text>
          )}

          {hasAttribution && (
            <Row alignItems="center" gap="$6x" $largerThanSM={ATTRIBUTION_DESKTOP}>
              {imageUrl ? (
                <CaseStudyImage imageUrl={imageUrl} alt={getImageAlt(entry)} />
              ) : (
                <Box
                  width="$40x"
                  height="$40x"
                  borderRadius="$full"
                  backgroundColor="$backgroundPrimary"
                  borderColor="$borderPrimary"
                  borderWidth={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text tag="span" typography="$labelXsmall" color="$contentTertiary">
                    (photo)
                  </Text>
                </Box>
              )}
              <Col gap="$1x">
                {quoteAuthor && (
                  <Row gap="$2x" alignItems="center" flexWrap="wrap">
                    <Text tag="p" typography="$labelLarge" color="$contentPrimary" display="block">
                      {quoteAuthor}
                    </Text>
                    {showVerifyBadge && <VerifyBadge />}
                  </Row>
                )}
                {quoteRole && (
                  <Text tag="p" typography="$bodySmall" color="$contentTertiary" display="block">
                    {quoteRole}
                  </Text>
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Col>
    </Container>
  );
};

const PartnerCaseStudy: EntryComponent<ILpComponentPartnerCaseStudy> = {
  render: RenderPartnerCaseStudy,
};

export default PartnerCaseStudy;
